import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import * as inboxApi from '../api/inbox';
import { useAuth } from '../lib/auth';
import { useToast } from '../components/toast';
import { ErrorAlert, Spinner, EmptyState, RelativeTime } from '../components/ui';
import { cn, ui } from '../lib/ui';

export default function ConversationDetail() {
  const { conversationId } = useParams();
  const { user } = useAuth();
  const toast = useToast();
  const bottomRef = useRef(null);

  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [replyBody, setReplyBody] = useState('');
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await inboxApi.getConversation(conversationId, { limit: 100 });
      setConversation(data.conversation);
      setMessages(data.messages || []);
      setError(null);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [conversationId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!loading && bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [loading, messages.length]);

  const otherParticipantId = conversation?.participantIds?.find(id => id !== user?.userId);

  async function sendReply(event) {
    event.preventDefault();
    if (!replyBody.trim() || !otherParticipantId) return;
    setSending(true);
    try {
      const { message } = await inboxApi.sendMessage({
        recipientId: otherParticipantId,
        body: replyBody.trim(),
      });
      setMessages(prev => [...prev, message]);
      setReplyBody('');
    } catch (err) {
      toast.error(err);
    } finally {
      setSending(false);
    }
  }

  if (loading) return <Spinner />;
  if (error) return <ErrorAlert error={error} />;
  if (!conversation) return <EmptyState>Conversation not found.</EmptyState>;

  return (
    <>
      <div className={cn(ui.row, ui.spread, 'mb-4')}>
        <div>
          <Link to="/messages" className={cn(ui.btnSecondary, 'mr-2')}>
            &larr; Back
          </Link>
          <strong className="text-body-l">
            Conversation with {otherParticipantId || 'unknown'}
          </strong>
        </div>
      </div>

      <div className="mb-4 max-h-[60vh] overflow-y-auto rounded-dcon-md border border-z-line p-4">
        {messages.length === 0 ? (
          <EmptyState>No messages yet.</EmptyState>
        ) : (
          messages.map(m => {
            const isMine = m.senderId === user?.userId;
            return (
              <div
                key={m.messageId}
                className={cn('mb-3 flex', isMine ? 'justify-end' : 'justify-start')}
              >
                <div
                  className={cn(
                    ui.card,
                    'max-w-[70%]',
                    isMine ? 'bg-dcon-brand-soft!' : 'bg-dcon-surface-sunken!'
                  )}
                >
                  {m.subject && <strong className="mb-1 block">{m.subject}</strong>}
                  <p className="m-0 whitespace-pre-wrap">{m.body}</p>
                  <div className={cn(ui.muted, 'mt-1 text-caption')}>
                    <RelativeTime iso={m.createdAt} />
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={bottomRef} />
      </div>

      <form onSubmit={sendReply} className="flex flex-wrap items-center gap-2 *:min-w-0">
        <textarea
          className={cn(ui.input, 'flex-1')}
          rows={2}
          placeholder="Type a message..."
          value={replyBody}
          onChange={e => setReplyBody(e.target.value)}
        />
        <button type="submit" className={ui.btnPrimary} disabled={sending || !replyBody.trim()}>
          {sending ? 'Sending...' : 'Send'}
        </button>
      </form>
    </>
  );
}
